<script setup lang='ts'>
import { computed } from 'vue';
import Select from '@/components/Form/Select.vue';
import { Button } from '@/components/ui/button';
import RuleRow from '@/components/FilterBuilder/RuleRow.vue';
import ListSearchRuleRow from '@/components/FilterBuilder/ListSearchRuleRow.vue';
import { LIST_SEARCH_RULE_ID } from '@/utils/listSearch';
import FavoriteFilterMenu from '@/components/FilterBuilder/FavoriteFilterMenu.vue';
import type { TableColumn } from '@/types/tableTypes';
import { isGroup } from '@/types/typeguards/QueryBuilder';
import type { IRule, IScope } from '@/types/filterBuilderTypes';
import type { FavoriteRuleSnapshot } from '@/utils/favoriteFilters';
import { GripVerticalIcon } from '@/components/ui/icons';
import DraggableContainer from '@/components/ui/draggable/DraggableContainer.vue';

const props = defineProps<{
  columnDefinitions: TableColumn[];
  depth: number;
}>();

defineEmits<{
  newRule: [groupId: string]
  newInnerRule: [ruleId: string]
  deleteRule: [ruleId: string]
  insertFavoriteRule: [groupId: string, rule: FavoriteRuleSnapshot]
  updateRulesOrder: [{ removed: {oldIndex: number, element: IScope}}]
  change: [event: { added?: { newIndex: number; element: IScope }; removed?: { oldIndex: number; element: IScope }; moved?: { newIndex: number; oldIndex: number; element: IScope } }]
}>();

const scope = defineModel<IScope>('scope', { required: true });

const logicOptions = [{ label: 'And', value: 'and' }, { label: 'Or', value: 'or' }];

const sortedColumns = computed(() => {
  // Show all filterable columns regardless of visibility
  const allRelevantColumns = props.columnDefinitions.filter(col =>
    col.isFilterable
  );

  return allRelevantColumns.sort((a, b) => {
    return a.header.localeCompare(b.header);
  });
});
</script>

<template>
  <!--
    Notion-style level layout. Every level establishes a CSS Grid with
    named tracks `[drag] auto [prefix] 72px [field] auto [op] auto
    [value] minmax(180px,1fr) [menu] auto`. The DraggableContainer and the
    Add-Filter row both use `grid-template-columns: subgrid` so each rule
    row, the group rows, and the Add-Filter row share the same column
    tracks. The widest field/operator content in a level expands the
    column for everyone — so all column controls in a level stay the same
    width.
  -->
  <div class="w-full">
    <template v-if="isGroup(scope) && depth < 4">
      <div :class="depth > 1 ? 'flex flex-col p-1.5 border text-secondary-foreground border-border bg-secondary/40 rounded-md' : 'flex flex-col py-1'">
        <div class="filter-rules-level">
          <DraggableContainer
            v-model="scope.rules"
            @change="($event.removed && typeof $event.removed.oldIndex === 'number') && $emit('updateRulesOrder', { removed: { oldIndex: $event.removed.oldIndex, element: $event.removed.element as IScope } })"
            item-key="id"
            class="filter-rules-container"
            :group="{name: scope.id, pull: true, put: true}"
            handle=".drag-handle"
          >
            <template #item="{ element: rule, index }">
              <!--
                Drag handle (col `drag`). Hidden on mobile because the
                stacked layout collapses each control to its own row and
                drag-and-drop is awkward at narrow widths.
              -->
              <div
                v-if="scope.rules.length > 1"
                class="cursor-move drag-handle hidden md:flex items-center justify-center self-start h-8"
                style="grid-column: drag-start / drag-end;"
              >
                <GripVerticalIcon size="sm" class="text-muted-foreground/60" />
              </div>
              <template v-if="isGroup(rule)">
                <!--
                  Group prefix (col `prefix`). On `md+` it sits in the 72px
                  right-aligned slot with `mt-2` to align with the first
                  inner row's prefix; on mobile it's a full-width left-
                  aligned label that reads as the group's row header.
                -->
                <div
                  class="flex items-center justify-start md:justify-end h-8 pe-1 md:mt-2 text-xs text-muted-foreground self-start"
                  style="grid-column: prefix-start / prefix-end;"
                >
                  <span v-if="index === 0" class="font-normal">Where</span>
                  <Select
                    v-else-if="index === 1"
                    size="sm"
                    class="text-xs"
                    data-testid="filter-logic-selector"
                    optionLabel="label"
                    optionValue="value"
                    v-model="scope.scopeType"
                    :options="logicOptions"
                  />
                  <span v-else class="font-normal">{{ scope.scopeType.charAt(0).toUpperCase() + scope.scopeType.slice(1) }}</span>
                </div>
                <!-- Nested group spans field..menu -->
                <div class="min-w-0 self-start" style="grid-column: field-start / menu-end;">
                  <FilterBody
                    :columnDefinitions="sortedColumns"
                    :depth="depth + 1"
                    :key="rule.id"
                    v-model:scope="scope.rules[scope.rules.findIndex(r => r.id === rule.id)]"
                    @deleteRule="v => $emit('deleteRule', v)"
                    @newInnerRule="v => $emit('newInnerRule', v)"
                    @newRule="v => $emit('newRule', v)"
                    @insertFavoriteRule="(groupId, favoriteRule) => $emit('insertFavoriteRule', groupId, favoriteRule)"
                    @updateRulesOrder="({ removed }) => $emit('updateRulesOrder', { removed })"
                  />
                </div>
              </template>
              <template v-else-if="rule && !isGroup(rule)">
                <!-- Rule prefix (col `prefix`). -->
                <div
                  class="flex items-center justify-start md:justify-end h-8 pe-1 text-xs text-muted-foreground"
                  style="grid-column: prefix-start / prefix-end;"
                >
                  <Select
                    v-if="index === 1"
                    size="sm"
                    class="text-xs"
                    data-testid="filter-logic-selector"
                    optionLabel="label"
                    optionValue="value"
                    v-model="scope.scopeType"
                    :options="logicOptions"
                  />
                  <span v-else class="font-normal">{{
                    index === 0
                      ? 'Where'
                      : (index > 1 ? (scope.scopeType.charAt(0).toUpperCase() + scope.scopeType.slice(1)) : '')
                  }}</span>
                </div>
                <!--
                  RuleRow renders its 4 cells (field, op, value, menu) via
                  `display: contents` so they become direct grid items of
                  this `.draggable-item` subgrid row.
                -->
                <ListSearchRuleRow
                  v-if="rule.id === LIST_SEARCH_RULE_ID"
                  v-model:rule="scope.rules[scope.rules.findIndex(r => r.id === rule.id)] as IRule"
                  @deleteRule="v => $emit('deleteRule', v)"
                />
                <RuleRow
                  v-else
                  v-model:rule="scope.rules[scope.rules.findIndex(r => r.id === rule.id)] as IRule"
                  :columns="sortedColumns"
                  :depth="depth"
                  @addInnerRule="v =>$emit('newInnerRule', v)"
                  @deleteRule="v => $emit('deleteRule', v)"
                />
              </template>
            </template>
          </DraggableContainer>
          <!--
            Add Filter row mirrors the same column tracks via subgrid on
            `md+`. On mobile the row stacks and the button sits left-aligned
            full-width like every other control.
          -->
          <div class="filter-rules-add-row mt-1">
            <div v-if="scope.rules.length > 1" class="hidden md:block" style="grid-column: drag-start / drag-end;"></div>
            <div style="grid-column: prefix-start / prefix-end;" class="flex items-center justify-start md:justify-end">
              <Button
                variant="link"
                class="h-6 text-xs px-1 gap-1 text-muted-foreground hover:text-foreground hover:no-underline whitespace-nowrap"
                type="button"
                data-testid="filter-add-rule-button"
                @click="$emit('newRule', scope.id)"
              >
                <!-- The + sits in the same 14px slot as Add Fav's star so the
                     two labels line up when the row stacks on mobile. -->
                <span class="flex h-3.5 w-3.5 items-center justify-center" aria-hidden="true">+</span>
                Add Filter
              </Button>
            </div>
            <!--
              "Add Fav" (SAU-260): right of "+ Add Filter" on md+ (next grid
              cell), stacked below it on mobile (the add-row is a flex column
              there). Inserting appends a pre-filled row to THIS group.
            -->
            <div style="grid-column: field-start / menu-end;" class="flex items-center justify-start">
              <FavoriteFilterMenu
                :columns="sortedColumns"
                @insert="favoriteRule => $emit('insertFavoriteRule', scope.id, favoriteRule)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <!--
        Single-rule scope (no group). Wrap in the same level grid so the
        prefix and rule cells follow the same column tracks as grouped rules.
      -->
      <div class="filter-rules-level">
        <div class="filter-rules-row">
          <div
            class="flex items-center justify-start md:justify-end h-8 pe-1 text-xs text-muted-foreground"
            style="grid-column: prefix-start / prefix-end;"
          >
            <span class="font-normal">Where</span>
          </div>
          <ListSearchRuleRow
            v-if="(scope as IRule).id === LIST_SEARCH_RULE_ID"
            v-model:rule="(scope as IRule)"
            @deleteRule="v => $emit('deleteRule', v)"
          />
          <RuleRow
            v-else
            v-model:rule="(scope as IRule)"
            :columns="sortedColumns"
            :depth="depth"
            @addInnerRule="v =>$emit('newInnerRule', v)"
            @deleteRule="v => $emit('deleteRule', v)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/*
  Mobile (default): every cell stacks vertically. Each rule (`.draggable-
  item`) is a flex column of its prefix + field + op + value + menu cells,
  with a soft divider between rules so they're visually distinct.
  `display: contents` on RuleRow's wrapper keeps its 4 cells as direct
  flex-column children of the rule.
*/
.filter-rules-level,
.filter-rules-level :deep(.filter-rules-container) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-rules-level :deep(.filter-rules-container) > .draggable-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px solid hsl(var(--border) / 0.6);
}

.filter-rules-level :deep(.filter-rules-container) > .draggable-item:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.filter-rules-add-row,
.filter-rules-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/*
  Desktop (`md+`): named-line column template — Notion-equivalent. The two
  `auto` tracks for `field` and `op` mean the column width is set by the
  widest cell across every row in the level (so all column-selectors share
  the same width, all operator-selectors share the same width, etc.).
  DraggableContainer's root and each `.draggable-item` use
  `grid-template-columns: subgrid` so they inherit the level's tracks
  while Sortable.js continues to operate on the DOM children unchanged.
*/
@media (min-width: 768px) {
  /*
    NOTE: do NOT add a `gap: 0` here. The `gap` shorthand sets both
    `row-gap` and `column-gap`, so a trailing `gap: 0` would silently
    cascade over the explicit `column-gap` below and erase the spacing
    between the field / criterion / value selectors (verified in the
    browser previously).
  */
  .filter-rules-level {
    display: grid;
    grid-template-columns:
      [drag-start] auto
      [drag-end prefix-start] 72px
      [prefix-end field-start] auto
      [field-end op-start] auto
      [op-end value-start] minmax(180px, 1fr)
      [value-end menu-start] auto
      [menu-end];
    /*
      `column-gap` controls the breathing room between the field / criterion
      / value selectors. The criterion dropdown reads tightly when it sits
      flush against its neighbours, so 10px gives it a clear visual buffer
      without making the row feel airy. Subgrids below inherit this gap.
    */
    column-gap: 10px;
    row-gap: 4px;
    align-items: center;
  }

  .filter-rules-level :deep(.filter-rules-container) {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: drag-start / menu-end;
    row-gap: 4px;
  }

  .filter-rules-level :deep(.filter-rules-container) > .draggable-item {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: drag-start / menu-end;
    column-gap: 10px;
    align-items: center;
    padding-bottom: 0;
    border-bottom: none;
  }

  .filter-rules-add-row,
  .filter-rules-row {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: drag-start / menu-end;
    column-gap: 10px;
    align-items: center;
  }
}
</style>
